import { collapsedLabel, collapsedLabelParts, restSpan, sectionName, setsFromRows, FORMAT_LABEL, formatHeadline,
         readFormatConfig, type SectionFormat, type SetRow } from "./builder";
import { wireNumber } from "./wireNumber";
import { phoneDose } from "./phoneDose";
import { distanceUnitFor } from "./distanceCell";
import type { AssignedWorkoutTree, TreeExercise, TreeSection, TreeSet } from "./assignedWorkoutTree";

/**
 * W17 — THE LIBRARY ROW'S PREVIEW, ONE LEVEL DOWN.
 *
 * The map's §18/§18a verdict, in its own words: *"the row preview shows the next level down…
 * the preview pattern is worth stealing"*. The Workouts row already prints the SHAPE —
 * `SHOULDER PREP (9) / BLOCK A — SKILL (2)` — so the level below that is the one a coach is
 * missing: which movements, at what dose.
 *
 * ⚠️ THIS SCREEN'S OWN HEADER SAYS "it does not preview", AND THAT SENTENCE IS NARROWED RATHER
 * THAN CONTRADICTED. §2b's rule is that a screen answers ONE question and that a control
 * belonging to a DIFFERENT question goes elsewhere — the example it gives is *assigning*, which
 * belongs to the client page because the client is the thing being chosen there. This screen's
 * one question is *which workout do I want*, and a list of the movements inside a template is
 * the most direct possible answer to it: the coach is choosing between `Push Skill · Pull
 * Strength A` and `B` and the difference lives one level down. An assign control would still be
 * wrong here. `Workouts.tsx`'s comment is edited in the same commit rather than left to
 * contradict the code beneath it — a doc-only claim that lapses with nothing red is this repo's
 * most-paid-for trap.
 *
 * 🔴 THE DOSE LINE IS THE BUILDER'S OWN, NOT A SECOND FORMATTER. `setsFromRows` → `columnsFor`
 * → `collapsedLabel` is exactly the chain `WorkoutBuilder` renders a collapsed row with, so the
 * preview cannot say `3 × 8 reps` about a row the builder opens as `3 × 10 reps`. Writing a
 * third sets-and-reps grammar (there are already two — the editor's and the athlete's
 * `AssistLoad.prescriptionLine`) would be the vocabulary-mismatch bug with a popover in front
 * of it.
 *
 * 🔴 IT NEEDS NOTHING FROM THE LIBRARY, AND THAT IS W17b's DOING. W17 threaded the exercises'
 * declared `tracking_fields` through this module and added a column to the library read to get
 * them, because `collapsedLabel` gated every measure on them. W17b measured the 18 live set rows
 * that gate was hiding — eleven of them his own calf raises at 15 kg — and the gate went, so the
 * map, the parameter and the extra column all went with it. **A parameter nothing reads is a
 * lie about what a function depends on**, and keeping it "in case" would have left the next
 * reader believing the preview consults the library. The door's tree is now the whole input.
 *
 * 🔴 W136 B2 (2026-10-05): `distance_m` IS IN THE DOOR'S SET OBJECT — the ninth key, added by migration
 * 20261005100000 (it was the one this comment used to say was absent). The bridge below reads it; a set without one
 * reads null ("not prescribed"), never a measured zero. The door still carries no tracking fields, so the preview prints
 * a distance in metres (the stored unit) — see `collapsedLabel`.
 */

/**
 * One exercise as it prints. `dose` is `collapsedLabel`'s sentence, never a number.
 *
 * 🔴 **THREE FIELDS ADDED FOR W22(f)'s TILES (2026-09-19), AND THE TRAVERSAL WAS NOT COPIED.**
 * The calendar's expanded day draws `Nx · NAME · dose` tiles and joins a superset into ONE tile;
 * both facts live on the row the door already returns. Writing a second walk of the tree to get
 * at them is the third grammar this module's own header refuses — so they are carried here and
 * the window, which needs neither, simply does not read them.
 */
export type PreviewRow = {
  name: string;
  dose: string;
  /** How many set rows this exercise has. `0` is a real state and `dose` says so in words. */
  sets: number;
  /** `coach_workout_exercises.superset_with_next`, `=== true` — a BOUNDARY flag, never a group. */
  supersetWithNext: boolean;
  /** The `coach_workout_exercises` row's own id — what W22(b)'s editor opens on. */
  exerciseRowId: string;
  /** The LIBRARY exercise (`coach_exercises.id`) — what the phone keys its media on (W66). */
  exerciseId: string | null;
  /** What the CLIENT'S overview prints under the name — `phoneDose`, the Swift's sentence (W66
   *  step 2). `dose` stays the BUILDER's grammar for the coach's own surfaces. */
  phoneDose: string;
  /** Fable fix (a) on W107: the Week card's dose — `collapsedLabel(sets, "short")`'s parts (`2 × 15`, `@ 12.5 kg`),
   *  the SAME function's short form, never a second formatter. `["No sets yet"]` for an exercise with none. */
  shortDoseParts: readonly string[];
  /** W137 — the coach's note on this exercise (`coach_workout_exercises.notes`, the words the client reads), trimmed; null
   *  when there is none. Drawn only by the Week's extended column (his 2026-10-02 word: *"plus any comments though (coach
   *  notes) and rest times. coach needs to see them too"*). */
  note: string | null;
  /** W137 — the rest as the builder's sentence says it (`restSpan`: `1:30`, `45s`, `60s–2:00`), or null when no set carries
   *  one. The column prints `Rest …`; the phone's own row never prints a rest, so the device frame does not read this. */
  rest: string | null;
};

/** One section. `more` is how many of its rows are NOT listed — stated, never silently dropped. */
export type PreviewSection = {
  /**
   * What to draw as the heading — **or `null`, which means DRAW NOTHING.**
   *
   * 🔴 §6.5's LOOSE RUN IS WHY THIS IS NULLABLE RATHER THAN A STRING. An implicit section draws
   * no bar and no name on either platform, and if this field were a string the only thing it
   * could hold for one is `""` or `Section 3` — the first is a name-shaped convention every
   * reader then has to know to test, the second labels a loose run as a section and is the very
   * thing §6.5 exists to stop. Absence is spelled as absence.
   */
  name: string | null;
  /** True for §6.5's implicit section. `name` is null when this is true — and (W104) for a section the coach never
   *  named: an unnamed section has NO generated name, so the coach's surfaces draw its exercises with no heading. */
  implicit: boolean;
  /** The format's own word — `Straight sets`, `EMOM` — from the ONE table in `builder.ts`. */
  formatLabel: string;
  /**
   * The format's NUMBER, as the builder's section bar prints it (`10 min`, `3 rounds`) — or null
   * when the format carries none. `formatHeadline`'s sentence, never a second formatter.
   */
  formatLine: string | null;
  /** The coach's own note on the section. Null and empty are one state to a reader; kept null. */
  description: string | null;
  /** The section's name AS STORED (null = the coach gave none) and its format key — the phone's
   *  briefing names an unnamed section by its own rule (`MAIN` / `Superset`), not `Section N`,
   *  so the phone twin (W66) needs the raw fact rather than `name`'s display word. */
  rawName: string | null;
  format: SectionFormat;
  /** W137 R3b — `coach_workout_sections.section_type` as stored (`warm_up`, `cool_down`, `workout`, `recovery`), so the
   *  extended Week's labels can say W1 / C1 BY TYPE, never by the section's name. Optional: absent reads as a main section. */
  sectionType?: string | null;
  rows: PreviewRow[];
  more: number;
};

export type WorkoutPreview = {
  /** The workout's own name and note, so a window drawing this needs no second read. */
  name: string;
  description: string | null;
  sections: PreviewSection[];
  /** Sections not listed. Same contract as `PreviewSection.more`. */
  more: number;
  /** The workout genuinely holds nothing. A DIFFERENT state from "not fetched yet". */
  empty: boolean;
};

/**
 * 🔴 A PREVIEW THAT LISTS EVERYTHING IS THE WORKOUT, NOT A PREVIEW — and it would be a worse
 * workout than the page one click away. His `Coverage Test` template carries 11 sections and 20
 * exercises; printed whole it is a scrolling popover that covers the list it is supposed to help
 * a coach read. The caps are what make it a glance.
 *
 * ⚠️ AND A CAP OWES A COUNT. Truncating to six rows and saying nothing tells a coach a section
 * HAS six rows, which is a lie the shape line one row up would contradict. Every cut is counted
 * and printed.
 */
export const PREVIEW_MAX_SECTIONS = 5;
export const PREVIEW_MAX_ROWS_PER_SECTION = 4;

/* ⚠️ AN UNNAMED SECTION IS NAMED BY THE SHARED RULE (`sectionName`), NOT BY A WORD OF THIS MODULE'S OWN.
   This file had `UNNAMED_SECTION = "Untitled section"` for an hour, and the render showed it
   sitting one line under the SAME section called `Section 1` by the row's shape line. The repair
   is the shared function, not a matching string. W104 (his word 2026-09-30): the rule now answers
   NULL for an unnamed section — no generated name on any coach surface. */
/** What a row whose library exercise the door could not resolve is called. */
export const UNNAMED_EXERCISE = "Untitled exercise";

/**
 * Order by `position`, nulls LAST, ties broken by `id`.
 *
 * ⚠️ THE DOOR CONTRACTUALLY ORDERS ITS EMBEDS (`order by s.position`, `e.position`,
 * `st.position` — all three are in the migration) AND THIS SORTS ANYWAY, deliberately. The
 * stand-in that renders this on the fixture does NOT sort: it filters the fixture's rows in dump
 * order, so a preview that trusted the contract would be measured against a tree that never
 * exercised it. A sort over already-sorted rows is free; the alternative is a check that is
 * clean and blind at once.
 */
export function byPosition<T extends { id: string; position: number | null }>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => {
    if (a.position === null && b.position === null) return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    if (a.position === null) return 1;
    if (b.position === null) return -1;
    if (a.position !== b.position) return a.position - b.position;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

/**
 * The door's set objects → the rows `setsFromRows` reads.
 *
 * `position` becomes the INDEX after the null-safe sort above, which is `rowsFromSets`' own
 * convention ("position is the INDEX, which is what makes the trip stable") — so a set row with
 * no position can never sort to the front of a ramp by scoring 0.
 */
export function setRows(sets: readonly TreeSet[]): SetRow[] {
  return byPosition(sets).map((st, i) => ({
    position: i,
    set_type: st.set_type ?? "regular",
    reps: st.reps,
    /* ⚠️ THE ONE PLACE THE DOOR'S WIDER TYPE IS NARROWED. `TreeSet.weight_kg` is
       `number | string | null` and `SetRow.weight_kg` is `number | null`, and the difference is
       real rather than cosmetic: a `numeric` is a JSON number over PostgREST and a STRING over
       the Management API, and the RPC's own `jsonb_build_object` is a third serialiser nobody
       here has measured. `wireNumber` reads all of them and is idempotent, so `setsFromRows`
       calling it again below costs nothing and neither call can be the only one. */
    weight_kg: wireNumber(st.weight_kg),
    time_seconds: st.time_seconds,
    rounds: st.rounds,
    rest_seconds: st.rest_seconds,
    // W136 B2 — the door carries it since 20261005100000 (`numeric`, so read through `wireNumber` like `weight_kg`).
    // An older door (or a row without one) answers undefined → null: "not prescribed", never a measured zero.
    distance_m: wireNumber(st.distance_m ?? null),
  }));
}

/** W136 B2 S3 — the coach exercise's `tracking_fields` by `exercise_id`, from the library read the page already makes (the door's
 *  exercise object carries only `name`): a run's unit is its FIELD's (`distance_km` → `5 km`). Absent = no coach fields. */
export type FieldsById = ReadonlyMap<string, readonly string[]>;

function previewRow(ex: TreeExercise, fields?: FieldsById): PreviewRow {
  const name = ex.coach_exercises?.name ?? null;
  const rows = setRows(ex.coach_workout_sets ?? []);
  const sets = setsFromRows(rows);
  const own = ex.exercise_id === null ? null : fields?.get(ex.exercise_id) ?? null;
  // W136 B2 S3 — the builder's label says a distance in the FIELD's unit too (RENDERED: `3 × 5000 m in 25:00` on a km row).
  const unit = distanceUnitFor(own ?? []);
  return {
    name: name === null || name.trim() === "" ? UNNAMED_EXERCISE : name,
    sets: sets.length,
    /* ⚠️ `=== true`, NEVER TRUTHINESS — the column is nullable on this type, and `null` there
       means "this door never said", which is not the claim `false` makes.
       🔴 **AND THE MUTANT `Boolean(ex.superset_with_next)` IS GENUINELY EQUIVALENT, WHICH IS
       RECORDED HERE RATHER THAN PAPERED OVER (2026-09-19).** It was run and it SURVIVED: over
       `boolean | null` the two expressions agree on all three values. `=== true` is kept because
       it survives a WIDENING of the type — the day the door returns a string or a number for
       this column, `Boolean("false")` is `true` and `=== true` is not — and because it states the
       claim. A clause asserting the spelling would pin a spelling, so there is none. */
    supersetWithNext: ex.superset_with_next === true,
    exerciseRowId: ex.id,
    exerciseId: ex.exercise_id,
    phoneDose: phoneDose(rows, own, ex.hold_until_lost === true),
    // ⚠️ AN EXERCISE WITH NO SETS IS A REAL STATE and `collapsedLabel` answers `""` for it
    // (`isUniform` is false on an empty list, then `sets[0]` is undefined). An empty dose would
    // render as a name with a dangling separator, so it is named here instead — the builder
    // calls the same case a validation problem, and a coach reading the preview should see the
    // same fact rather than a blank.
    dose: sets.length === 0 ? "No sets yet" : collapsedLabel(sets, "full", unit),
    shortDoseParts: sets.length === 0 ? ["No sets yet"] : collapsedLabelParts(sets, "short", unit),
    note: (ex.notes ?? "").trim() === "" ? null : (ex.notes ?? "").trim(),
    rest: restSpan(sets),
  };
}

function previewSection(sec: TreeSection, maxRows: number | null, fields?: FieldsById): PreviewSection {
  const all = byPosition(sec.coach_workout_exercises ?? []);
  const shown = maxRows === null ? all : all.slice(0, Math.max(0, maxRows));
  /* ⚠️ `=== true`, NEVER TRUTHINESS. The column is nullable on this type because a tree read
     before `20260919061000` carries no key at all, and `null` there means "this door never said",
     which is not the same claim as `false`. Both draw a heading; only one of them knows why. */
  const implicit = sec.is_implicit === true;
  const format = (sec.format ?? "straight_sets") as SectionFormat;
  const label = FORMAT_LABEL[format] as string | undefined;
  const description = (sec.description ?? "").trim();
  return {
    // W104 — the coach's name or NOTHING (never `Section N` / `Part N`): an unnamed section's rows draw with no heading.
    name: implicit ? null : sectionName(sec.name),
    implicit,
    /* ⚠️ A FORMAT THE TABLE DOES NOT KNOW FALLS BACK TO STRAIGHT SETS' WORD RATHER THAN PRINTING
       ITS RAW VALUE. `format` arrives from the database as a plain string, so the CHECK constraint
       is the only thing keeping it inside the union and a future value would reach here as
       `undefined` off the table — which renders as the word "undefined" in a coach's window. */
    formatLabel: label ?? FORMAT_LABEL.straight_sets,
    // The builder's own sentence, off the builder's own config reader — so a section cannot say
    // `10 min` in the window and something else in the editor one click away.
    formatLine: formatHeadline(format, readFormatConfig(format, sec.format_config)),
    description: description === "" ? null : description,
    rawName: (sec.name ?? "").trim() === "" ? null : (sec.name ?? "").trim(),
    format,
    sectionType: sec.section_type ?? null,
    rows: shown.map((ex) => previewRow(ex, fields)),
    more: all.length - shown.length,
  };
}

/** The whole preview, pure. The door's tree is the only input; see the header on why. */
export function workoutPreview(
  tree: AssignedWorkoutTree,
  /**
   * ⚠️ `null` MEANS NO CAP, AND IT IS SPELLED AS ABSENCE RATHER THAN AS A HUGE NUMBER. The codex
   * rule is one day old and was written about exactly this shape: *"a sentinel that is only safe
   * until somebody adds to it is not a sentinel — if a value means ABSENCE, spell it as absence"*.
   * `Number.MAX_SAFE_INTEGER` here would survive `slice` and trap the first time anything wrote
   * `cursor + maxRows`. `undefined` keeps meaning "use the glance's default".
   */
  limits: { maxSections?: number | null; maxRows?: number | null } = {},
  fields?: FieldsById,
): WorkoutPreview {
  const maxSections = limits.maxSections === undefined ? PREVIEW_MAX_SECTIONS : limits.maxSections;
  const maxRows = limits.maxRows === undefined ? PREVIEW_MAX_ROWS_PER_SECTION : limits.maxRows;
  const all = byPosition(tree.coach_workout_sections ?? []);
  const shown = maxSections === null ? all : all.slice(0, Math.max(0, maxSections));
  const sections = shown.map((s) => previewSection(s, maxRows, fields));
  const description = (tree.description ?? "").trim();
  return {
    name: tree.name,
    description: description === "" ? null : description,
    sections,
    more: all.length - shown.length,
    // ⚠️ EMPTY IS ABOUT THE WORKOUT, NOT ABOUT THE PREVIEW. A template with eleven sections and
    // no exercises in any of them is not empty — it is a workout under construction, and the
    // section names are exactly what a coach wants to see. Only a tree with no sections at all
    // says so.
    empty: all.length === 0,
  };
}

/**
 * W17d — THE WHOLE WORKOUT, UNCAPPED, for the window the row's hover icon opens.
 *
 * 🔴 IT IS THE SAME FUNCTION WITH THE CAPS OFF, NOT A SECOND BUILDER. His word (§7.1): *"that
 * first view of hovering over we should be able to see as much as possible of the workout in 1
 * place, and tidy"* — so nothing may be cut, and the `+N more` lines can never appear (both
 * `more` counts are 0 by construction here, which a test asserts). Writing a separate traversal
 * for the window would be the third grammar this module's own header refuses.
 */
export function workoutOverview(tree: AssignedWorkoutTree, fields?: FieldsById): WorkoutPreview {
  return workoutPreview(tree, { maxSections: null, maxRows: null }, fields);
}

/** The door's answer → what the WINDOW draws. Same contract as `previewState`, uncapped. */
export function overviewState(
  result: TreeResult,
  fields?: FieldsById,
): { kind: "ready"; preview: WorkoutPreview } | { kind: "error"; message: string } {
  if (result.tree === null) return { kind: "error", message: result.error };
  // W136 B2 S3r4 W1 — `fields` from `readLibraryRows`: a km run says `6 km` here as it does in the week column.
  return { kind: "ready", preview: workoutOverview(result.tree, fields) };
}

/** What the door answered, in one value — the shape `fetchAssignedWorkoutTree` returns. */
export type TreeResult =
  | { tree: AssignedWorkoutTree; error: null }
  | { tree: null; error: string };

/**
 * The door's answer → what the panel draws.
 *
 * ⚠️ EXTRACTED FROM THE HOOK ON PURPOSE. This package has NO DOM test environment — no jsdom, no
 * testing-library, and every test in `src/lib` is on a pure module — so a decision left inside a
 * React hook has no runner at all. Everything that DECIDES anything lives here; the hook is left
 * holding `useState`, a `useRef` and the once-only guard, which is React plumbing rather than a
 * rule. **The gap is stated rather than papered over: `useWorkoutPreviews` and
 * `usePreviewTrigger` are not executed by any test, and adding a DOM environment is its own
 * change.**
 */
export function previewState(
  result: TreeResult,
): { kind: "ready"; preview: WorkoutPreview } | { kind: "error"; message: string } {
  if (result.tree === null) return { kind: "error", message: result.error };
  return { kind: "ready", preview: workoutPreview(result.tree) };
}

/** How the remainder reads. One sentence, so the two `more` counts cannot drift apart. */
export function moreLine(count: number, noun: "section" | "exercise"): string | null {
  if (count <= 0) return null;
  return `+${count} more ${noun}${count === 1 ? "" : "s"}`;
}
